


























































































































import {
  defineComponent,
  reactive,
  ref,
  onBeforeMount,
  computed,
  watch,
} from '@vue/composition-api'
import moment from 'moment'
import { VueLoading } from 'vue-loading-template'

import { IUser } from '@/types/user'
import { IItem, IReceipt, NgReason } from '@/types/receipt'
import { IShop } from '@/types/shop'
import { ReceiptState, UserState } from '@/types/store'
import { extractNgText } from '@/utils/extractNgText'
import { getLog, okOrNg } from '@/utils/changeReceiptStatus'
import { generateItem } from '@/utils/generator'
import { receiptTypeToDisplayName } from '@/utils/mapper'
import {
  hasNg,
  hasNonShopPhoneDateTimeNg,
  validatePhoneInfo,
  validateShopInfo,
  validateRegisterShopInfo,
  validateDateTimeInfo,
  validateProductInfo,
  validatePaymentInfo,
  validatePointInfo,
} from '@/utils/validation'
import useVuex from '@/utils/useVuex'

import { ReceiptView } from '@/components/molecules'
import {
  AbstractTable,
  OcrActions,
  DetailTable,
  SummaryTable,
  NextActions,
  MetaTable,
} from '@/components/organisms/ocr'
import Layout from '@/components/organisms/common/Layout.vue'
import { ApprovalResult, LogType } from '@/types/log'

export default defineComponent({
  name: 'Ocr',
  components: {
    ReceiptView,
    AbstractTable,
    OcrActions,
    DetailTable,
    SummaryTable,
    NextActions,
    MetaTable,
    Layout,
    VueLoading,
  },
  setup(props, context) {
    const store = context.root.$store
    const state = reactive({
      receiptType: useVuex<ReceiptState['receiptType']>(
        context,
        () => store.getters['receipt/receiptType'],
      ),
      receipt: useVuex<ReceiptState['receipt']>(
        context,
        () => store.getters['receipt/receipt'],
      ),
      receipts: useVuex<ReceiptState['receipts']>(
        context,
        () => store.getters['receipt/receipts'],
      ),
      user: useVuex<UserState['user']>(
        context,
        () => store.getters['user/user'],
      ),
      workStartedMoment: moment(),
      isModified: false,
      isBasicInfoModified: false,
      isShopModified: false,
      loading: false,
    })
    const validationTextOk = 'すべての項目を入力済です'
    const validationTextNg = '未入力の項目があります'
    onBeforeMount(() => {
      if (state.receipts.length === 0) {
        context.root.$router.push('/').catch(() => {
          /*2連打した際のerrorを無視*/
        })
        return
      }
    })
    const isOkReceipt = computed<boolean>(() => {
      if (state.receipt === null) return false
      return (
        ['uncheckedSystemNg', 'systemNg', 'shopUnchecked'].indexOf(
          state.receiptType,
        ) === -1
      )
    })
    const ngText = computed<string>(() => {
      if (state.receipt === null) return ''
      return extractNgText(state.receipt)
    })

    // validations
    const validation = computed<Record<string, boolean>>(() => {
      return {
        hasNg: hasNg(state.receipt),
        invalidShopInfo: !validateShopInfo(state.receipt),
        invalidPhoneInfo: !validatePhoneInfo(state.receipt),
        invalidDateTimeInfo: !validateDateTimeInfo(state.receipt),
        invalidProductInfo: !validateProductInfo(state.receipt),
        invalidPaymentInfo: !validatePaymentInfo(state.receipt),
        invalidPointInfo: !validatePointInfo(state.receipt),
      }
    })
    const isNormalValidationNg = computed<boolean>(() => {
      if (validation.value.hasNg) {
        return false
      }
      return Object.keys(validation.value).some(
        key => (validation.value as Record<string, boolean>)[key],
      )
    })
    const isShopRegisterValidationNg = computed<boolean>(() => {
      return (
        hasNonShopPhoneDateTimeNg(state.receipt) ||
        !validatePhoneInfo(state.receipt) ||
        !validateRegisterShopInfo(state.receipt) ||
        !validateDateTimeInfo(state.receipt)
      )
    })
    const isValidationNg = computed<boolean>(() => {
      if (validation.value.hasNg) {
        // OK
        return false
      }
      if (
        state.receiptType === 'uncheckedSystemNg' ||
        state.receiptType === 'systemNg' ||
        state.receiptType === 'shopUnchecked'
      ) {
        return isShopRegisterValidationNg.value
      }
      return isNormalValidationNg.value
    })

    // For dynamic receipt view height
    const receiptViewHeight = ref<number>(0)
    const changeReceitViewHeight = () => {
      setTimeout(() => {
        const rightCol = document.querySelector('.Ocr_RightCol')
        receiptViewHeight.value = rightCol ? rightCol.scrollHeight - 76 : 0
      }, 100)
    }
    watch(
      () => state.receipt?.id,
      () => {
        if (state.receipt !== null) {
          state.loading = false
          changeReceitViewHeight()
        }
      },
    )
    changeReceitViewHeight()

    // Erros
    const apiError = ref<string>('')
    watch(
      () => state.receipt,
      () => {
        apiError.value = ''
      },
    )
    const onClickApiErrorClose = () => {
      apiError.value = ''
    }

    // logger
    watch(
      () => state.receipt?.id,
      () => {
        state.workStartedMoment = moment()
        state.isModified = false
        state.isBasicInfoModified = false
      },
    )

    const originalPhone = ref<string | null>(
      state.receipt === null ? null : `${state.receipt.phone}`,
    )
    watch(
      () => state.receipt?.id,
      () => {
        originalPhone.value =
          state.receipt === null ? null : `${state.receipt.phone}`
      },
    )
    const originalNgReason = ref<NgReason | null>(
      state.receipt === null
        ? null
        : state.receipt.otherNgReason
        ? NgReason.OTHER
        : state.receipt.ngReason,
    )
    watch(
      () => state.receipt?.id,
      () => {
        originalNgReason.value =
          state.receipt === null
            ? null
            : state.receipt.otherNgReason
            ? NgReason.OTHER
            : state.receipt.ngReason
      },
    )

    const updateReceipt = (receipt: IReceipt) => {
      if (receipt.phone !== state.receipt?.phone) {
        state.isBasicInfoModified = true
      }
      if (receipt.shopId !== state.receipt?.shopId) {
        state.isBasicInfoModified = true
      }
      if (!moment(receipt.dateTime).isSame(moment(state.receipt?.dateTime))) {
        state.isBasicInfoModified = true
      }
      if (receipt.payment !== state.receipt?.payment) {
        state.isBasicInfoModified = true
      }
      if (receipt.point !== state.receipt?.point) {
        state.isBasicInfoModified = true
      }

      state.receipt = receipt
    }

    // Items update actions
    const updateItems = (items: IItem[]) => {
      if (state.receipt === null) return
      state.receipt = {
        ...state.receipt,
        items: items.map(item => {
          if (!item.quantity) {
            return {
              ...item,
              quantity: 1,
            }
          }
          return item
        }),
      }
      state.isModified = true
    }

    // Table add delete actions
    const addItem = (index: number) => {
      if (state.receipt === null) return
      const newItem = generateItem()
      state.receipt.items.splice(index, 0, newItem)
      state.isModified = true
      changeReceitViewHeight()
    }
    const deleteItem = (index: number) => {
      if (state.receipt === null) return
      state.receipt.items.splice(index, 1)
      state.isModified = true
      changeReceitViewHeight()
    }

    // Save or Unsave receipt status
    const saveReceipt = async () => {
      if (state.receipt !== null && state.user !== null) {
        const newReceipt = okOrNg(state.receipt, state.user, state.receiptType)
        apiError.value = await store.dispatch(
          'receipt/updateReceipt',
          newReceipt,
        )
        if (apiError.value.length > 0) {
          throw new Error(apiError.value)
        }
        return newReceipt
      }
      return null
    }
    const saveLog = async (
      receipt: IReceipt,
      user: IUser,
      newReceipt: IReceipt,
      logType: LogType,
    ) => {
      const now = moment()
      const log = getLog(receipt, state.receiptType)
      let approvalResult: ApprovalResult | null = null
      if (logType === LogType.SHOP) {
        approvalResult = state.isShopModified
          ? ApprovalResult.RE_ITEM
          : ApprovalResult.APPROVED
      }
      await store.dispatch('log/createLog', {
        logType,
        workedById: user.id,
        castId: user.castId,
        castName: user.castName,
        workerType: user.role,
        receiptId: newReceipt.id,
        workedAt: now.toDate(),
        workDuration: now.diff(state.workStartedMoment, 'seconds'),
        workTarget: log[0],
        isModified: state.isModified,
        workResult: log[1],
        approvalResult,
        count: 1,
        isBasicInfoModified: state.isBasicInfoModified,
        linkedItems: newReceipt.items.filter(i => i.isChanged).length,
        isRewarded: user.isRewarded,
        ngReasonFrom: originalNgReason.value,
        ngReasonTo: newReceipt.otherNgReason
          ? NgReason.OTHER
          : newReceipt.ngReason,
        note: '',
      })
    }
    const finishReceipt = async () => {
      if (state.receipt !== null && state.user !== null) {
        if (state.receiptType === 'uncheckedSystemNg') {
          if (
            state.receipt.shop === null &&
            state.receipt.ngReason === NgReason.NOT_FOUND_SHOP &&
            state.receipt.phone !== null
          ) {
            state.receipt.ngReason = NgReason.INVALID_CHAIN_STORE
            // システムNG残からの変更で、NG理由がチェーン店マスタなしの場合、
            // shopマスタ未登録なので、対象外チェーンとしてマスタ登録する
            const shopToBeCreated: Partial<IShop> = {
              id: undefined,
              name: '',
              group_name: '',
              corporate_name: '',
              zip: '',
              tel: state.receipt.phone,
              prefecture: '',
              address: '',
              latitude: 0,
              longitude: 0,
              chain_store_id: 982, // レシート投稿対象外チェーン
              leaderAppliedAt: new Date(),
              appliedByLeaderId: state.user.id,
              adminApprovedAt: new Date(),
              approvedByAdminId: state.user.id,
            }
            try {
              const createdShop: IShop = await store.dispatch(
                'shop/postShop',
                shopToBeCreated,
              )
              state.receipt.shopId = createdShop.id
              state.receipt.shop = createdShop
              state.receipt.chainStoreId = createdShop.chainStore.id
              state.receipt.chainStore = createdShop.chainStore
              state.receipt.channelId = createdShop.chainStore.channel.id
              state.receipt.channel = createdShop.chainStore.channel
            } catch (_) {
              console.error(_)
            }
          }
        }
      }
      const newReceipt = await saveReceipt()
      if (
        state.receipt !== null &&
        newReceipt !== null &&
        state.user !== null
      ) {
        if (state.receiptType === 'shopUnchecked') {
          const shop: Partial<IShop> = {
            ...state.receipt.shop,
            adminApprovedAt: new Date(),
            approvedByAdminId: state.user.id,
          }
          store.dispatch('shop/putShop', shop)
          await saveLog(state.receipt, state.user, newReceipt, LogType.SHOP)
        } else {
          await saveLog(state.receipt, state.user, newReceipt, LogType.RECEIPT)
        }
      }
    }

    const onClickFinishWithSave = async () => {
      state.loading = true
      try {
        await finishReceipt()
        context.root.$router.push('/').catch(() => {
          /*2連打した際のerrorを無視*/
        })
      } catch (error) {
        console.error(error)
      }
    }
    const onClickFinishWithoutSave = () => {
      context.root.$router.push('/').catch(() => {
        /*2連打した際のerrorを無視*/
      })
    }
    const onClickGoBackWithoutSave = () => {
      context.root.$router.go(-1)
    }

    // Go other receipt actions
    const receiptIndex = computed(() => {
      if (state.receipts) {
        return state.receipts.findIndex(
          receipt => receipt.id === state.receipt?.id,
        )
      }
      return -1
    })
    const isLastReceipt = computed(
      () => receiptIndex.value + 1 === (state.receipts || []).length,
    )
    const isFirstReceipt = computed(() => receiptIndex.value === 0)
    const onClickNextReceipt = async () => {
      state.loading = true
      try {
        await finishReceipt()
        const receipt = state.receipts[receiptIndex.value + 1]
        if (receipt) {
          store.dispatch('receipt/getReceiptWithReProcess', receipt.id)
          state.loading = false
        } else {
          context.root.$router.go(-1)
        }
      } catch (error) {
        console.error(error)
      }
    }
    const onClickPrevReceipt = () => {
      const receipt = state.receipts[receiptIndex.value - 1]
      store.dispatch('receipt/getReceipt', receipt.id)
      state.loading = true
    }

    const title = `${receiptTypeToDisplayName(
      state.receiptType || null,
    )}レシート | OCRマッチング確認`

    return {
      state,
      validationTextOk,
      validationTextNg,
      isOkReceipt,
      ngText,
      receiptViewHeight,
      apiError,
      onClickApiErrorClose,
      updateReceipt,
      updateItems,
      addItem,
      deleteItem,
      validation,
      isValidationNg,
      isFirstReceipt,
      isLastReceipt,
      onClickFinishWithSave,
      onClickFinishWithoutSave,
      onClickGoBackWithoutSave,
      onClickPrevReceipt,
      onClickNextReceipt,
      title,
      originalPhone,
    }
  },
})
